import { faFilm, faImage } from '@fortawesome/free-solid-svg-icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Control, UseFormClearErrors, UseFormWatch } from 'react-hook-form';

import { MediaType } from 'apps/shared/scripts/__generated__/graphql.generated';
import { ModalSection, PillButton } from 'apps/shared/scripts/components';
import { PRO_MODE_ENABLED } from 'apps/shared/scripts/feature-gating/constants';
import useFeatureGate from 'apps/shared/scripts/feature-gating/hooks';

import { FORM_FIELDS } from '../constants';
import { FormValues } from '../types';

import GifField from './GifField';
import ImageField from './ImageField';
import messages from './messages.json';
import { buttonGroupClassName, spacingClassName } from './styles';

interface Props {
  control: Control<FormValues>;
  watch: UseFormWatch<FormValues>;
  clearErrors: UseFormClearErrors<FormValues>;
}

const MediaField = ({ watch, clearErrors, ...props }: Props) => {
  const media = watch(FORM_FIELDS.MEDIA.name);
  const [active, setActive] = useState<MediaType | null>(media?.type ?? null);
  const [prev, setPrev] = useState<MediaType | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const onToggle = useCallback(
    (tab: MediaType) => {
      setPrev(active);
      if (tab === active) {
        setActive(null);
      } else {
        setActive(tab);
      }
    },
    [active],
  );

  useEffect(() => {
    if (ref.current && active && !media) {
      ref.current.scrollIntoView();
    }
  }, [active, media, prev]);

  useEffect(() => {
    if (media) {
      clearErrors();
    }
  }, [media, clearErrors]);

  const isProModeEnabled = useFeatureGate(PRO_MODE_ENABLED);

  return (
    <ModalSection
      title={messages.title}
      description={isProModeEnabled ? messages.description : ''}
      spacing
    >
      {!media && (
        <div className={buttonGroupClassName} ref={ref}>
          <PillButton
            active={active === MediaType.Giphy}
            faIcon={faFilm}
            label={'GIF'}
            onClick={() => onToggle(MediaType.Giphy)}
          />
          {isProModeEnabled && (
            <PillButton
              active={active === MediaType.Image}
              faIcon={faImage}
              label={'Image'}
              onClick={() => onToggle(MediaType.Image)}
            />
          )}
        </div>
      )}
      {active && (
        <div className={spacingClassName}>
          {active === MediaType.Giphy && <GifField {...props} />}
          {active === MediaType.Image && <ImageField {...props} />}
        </div>
      )}
    </ModalSection>
  );
};

MediaField.displayName = 'MediaField';

export default MediaField;
